import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Clock, CheckCircle2, AlertCircle, Send } from "lucide-react";
import PublicLayout from "./PublicLayout";
import { AURORA_SUPPORT_EMAIL as EMAIL } from "../../lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit =
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    message.trim().length > 0 &&
    status !== "sending";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/contact/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          from_email: email.trim(),
          message: message.trim(),
        }),
      });
      if (res.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setMessage("");
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMsg(
          data.detail ||
            "Something went wrong. Please try again in a moment."
        );
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <PublicLayout>
      <section
        className="relative overflow-hidden"
        data-testid="page-contact"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FF3B6B]/[0.08] blur-[140px]" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#7C3AED]/[0.07] blur-[140px]" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-40">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] mb-10 transition-colors"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            data-testid="contact-back-home"
          >
            <ArrowLeft size={12} />
            Back to Aurora
          </Link>

          <p
            className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.3em] mb-5"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Get in touch
          </p>

          <h1
            style={{ fontFamily: "'Bodoni Moda', serif" }}
            className="text-5xl sm:text-6xl md:text-7xl tracking-tighter text-white leading-[1.05]"
            data-testid="contact-title"
          >
            Contact
          </h1>

          <div className="mt-8 h-[1px] w-16 bg-gradient-to-r from-[#FF3B6B] to-transparent" />

          <p
            className="mt-8 text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Send us a message — a real human on our team reads every one and
            replies within 24 hours. No copying, no email apps, no friction.
          </p>

          {/* Response time */}
          <div
            data-testid="contact-response-time"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full pl-3 pr-5 h-10 bg-white/[0.04] border border-white/10"
          >
            <div className="w-5 h-5 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center">
              <Clock size={10} className="text-[#00FF66]" />
            </div>
            <span
              className="text-white/80 text-sm"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              We reply{" "}
              <span className="text-white font-medium">within 24 hours</span>
            </span>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="mt-10 rounded-2xl border border-white/[0.10] bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-6 md:p-8 space-y-5"
          >
            {/* To (locked pill) */}
            <div>
              <label
                className="block text-white/50 text-[10px] uppercase tracking-[0.25em] mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                To
              </label>
              <div className="flex items-center gap-2.5 rounded-lg px-4 h-12 bg-white/[0.04] border border-white/10">
                <Mail size={14} className="text-[#FF3B6B]" />
                <span
                  className="text-white/85 text-sm break-all"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  data-testid="contact-form-to"
                >
                  {EMAIL}
                </span>
              </div>
            </div>

            {/* Name */}
            <div>
              <label
                htmlFor="cf-name"
                className="block text-white/50 text-[10px] uppercase tracking-[0.25em] mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Your name
              </label>
              <input
                id="cf-name"
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="contact-form-name"
                className="w-full rounded-lg px-4 h-12 bg-black/40 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF3B6B]/60 transition-colors"
                placeholder="Jane Smith"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              />
            </div>

            {/* From email */}
            <div>
              <label
                htmlFor="cf-email"
                className="block text-white/50 text-[10px] uppercase tracking-[0.25em] mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Your email (so we can reply)
              </label>
              <input
                id="cf-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="contact-form-email"
                className="w-full rounded-lg px-4 h-12 bg-black/40 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF3B6B]/60 transition-colors"
                placeholder="jane@example.com"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              />
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="cf-msg"
                className="block text-white/50 text-[10px] uppercase tracking-[0.25em] mb-2"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Your message
              </label>
              <textarea
                id="cf-msg"
                required
                maxLength={10000}
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="contact-form-message"
                className="w-full rounded-lg px-4 py-3 bg-black/40 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF3B6B]/60 transition-colors resize-none"
                placeholder="Tell us what's on your mind — questions, feedback, safety concerns, press, anything."
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              />
            </div>

            {/* Status banners */}
            {status === "success" && (
              <div
                data-testid="contact-form-success"
                className="flex items-start gap-3 rounded-lg p-4 bg-[#0F3E1F] border border-[#1A4D2E]"
              >
                <CheckCircle2 size={18} className="text-[#00FF66] shrink-0 mt-0.5" />
                <div>
                  <p
                    className="text-[#9EE5B5] text-sm font-medium"
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    Message sent
                  </p>
                  <p
                    className="text-[#9EE5B5]/80 text-xs mt-0.5"
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    We'll reply within 24 hours.
                  </p>
                </div>
              </div>
            )}
            {status === "error" && (
              <div
                data-testid="contact-form-error"
                className="flex items-start gap-3 rounded-lg p-4 bg-[#3E0F1F] border border-[#4D1A2E]"
              >
                <AlertCircle size={18} className="text-[#FF3B6B] shrink-0 mt-0.5" />
                <p
                  className="text-[#FF9AB4] text-sm"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {errorMsg}
                </p>
              </div>
            )}

            {/* Send button */}
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="contact-form-submit"
              className={`inline-flex items-center justify-center gap-2 w-full rounded-lg h-12 font-medium text-sm tracking-wide transition-all ${
                canSubmit
                  ? "bg-[#FF3B6B] text-white hover:bg-[#E8335F]"
                  : "bg-white/[0.06] text-white/40 cursor-not-allowed"
              }`}
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              <Send size={14} />
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>

          {/* Safety note */}
          <div className="mt-12 p-6 rounded-2xl border border-white/[0.08] bg-white/[0.02]">
            <p
              className="text-white/50 text-[11px] uppercase tracking-[0.25em] mb-2"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Urgent safety concern?
            </p>
            <p
              className="text-white/75 text-sm leading-relaxed"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              If you or another Aurora member is in immediate danger, contact
              your local emergency services first, then use the form above with
              the word <span className="text-white">SAFETY</span> at the start
              of your message — we escalate those within one hour.
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
